import React, { useEffect, useRef, useState } from 'react';
import '../styles/TestimonialsAdmin.css';
import { FaPlusCircle, FaSearch } from 'react-icons/fa';
import { RiCheckFill, RiCloseFill, RiFileShield2Line, RiToggleFill, RiToggleLine, RiArrowUpSLine, RiArrowDownSLine } from 'react-icons/ri';
import { useSlideout } from '../../../hooks/SlideoutContext';
import axios from 'axios';
import config from '../../../constants/config';
import { IoMdRefresh } from 'react-icons/io';
import axiosPost from '../../../helper/axios-helper';
import { useOutletContext } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useToast } from '../../../hooks/ToastContext';

export default function TestimonialsAdmin() {
    const { account } = useOutletContext() || {};
    const [cookies] = useCookies(["account-canto"]);
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(false);
    const { openSlideout, closeSlideout } = useSlideout();
    const { showToast } = useToast();

    useEffect(() => {
        fetchTestimonials();
    }, []);

    const fetchTestimonials = () => {

        axiosPost('testimonials/fetch', {}, {}, (data) => {
            const { success } = data;
            if (success) {
                setTestimonials(data.data);
                console.log(data.data);
            } else {
                // Optionally handle error state here
            }
        });
    };

    const moveItem = (index, direction) => {
        setTestimonials((prev) => {
            const newList = [...prev];
            const targetIndex = index + direction;
            if (targetIndex < 0 || targetIndex >= newList.length) return prev;
            const temp = newList[index];
            newList[index] = newList[targetIndex];
            newList[targetIndex] = temp;
            return newList;
        });
    };

    const saveOrder = () => {
        if (loading) return;
        setLoading(true);
        const order = testimonials.map(t => t.id);
        axios.post(`${config.api.url}admin/testimonials/reorder`, { order }, {
            crossdomain: true,
            headers: {
                api: config.api.key,
                auth: account?.auth_token || cookies['account-canto']
            }
        }).then((res) => {
            const { success, response } = res.data;
            if (!success) {
                showToast({ 
                    message: 'Failed to save order: ' + (response || 'Unknown error'), 
                    type: 'error' 
                });
            } else {
                // Refetch to reflect any canonical ordering from server
                fetchTestimonials();
                showToast({ 
                    message: 'Testimonial order saved successfully!', 
                    type: 'success' 
                });
            }
            setLoading(false);
        }).catch((err) => {
            console.error('Reorder error:', err);
            showToast({ 
                message: 'Error saving order. Please try again.', 
                type: 'error' 
            });
            setLoading(false);
        });
    };

    const EditTestimonial = ({ testimonial = {}, fetchTestimonials }) => {
        const { closeSlideout } = useSlideout();
        console.log('Testimonial data:', testimonial);
        const [testimonialData, setTestimonialData] = useState({
            id: testimonial.id || '',
            client_name: testimonial?.client?.name || "",
            client_role: testimonial?.client?.role || "",
            testimonial: testimonial?.testimonial || "",
            rating: testimonial?.rating || 5,
            active: testimonial?.active === "1",
            client_image: testimonial?.client?.url || ""
        });

        const [loading, setLoading] = useState(false);
        const [selectedImageFile, setSelectedImageFile] = useState(null);
        const [selectedImagePreview, setSelectedImagePreview] = useState(testimonial.client_image);
        const fileInputRef = useRef(null);

        // Handle input changes
        const handleChange = (e) => {
            const { name, value, type, checked } = e.target;
            setTestimonialData((prev) => ({
                ...prev,
                [name]: type === "checkbox" ? checked : value
            }));
        };

        // Handle file selection
        const handleImageChange = (event) => {
            const file = event.target.files[0];
            if (file) {
                const imageUrl = URL.createObjectURL(file);
                setSelectedImageFile(file);
                setSelectedImagePreview(imageUrl);
            }
        };

        // Open file input
        const handleSelectImage = () => {
            fileInputRef.current.click();
        };

        // Handle save changes
        const handleSaveChanges = () => {
            // Set loading state
            setLoading(true);
            
            const formData = new FormData();
            const isNewItem = !testimonialData.id;
            
            // Only append ID if it's an update operation
            if (!isNewItem) {
                formData.append("id", testimonialData.id);
            }
            
            formData.append("client_name", testimonialData.client_name);
            formData.append("client_role", testimonialData.client_role);
            formData.append("testimonial", testimonialData.testimonial);
            formData.append("rating", testimonialData.rating); // Use the rating from state
            formData.append("active", testimonialData.active ? 1 : 0);

            if (selectedImageFile) {
                formData.append("client_image", selectedImageFile);
            }

            // Log the form data for debugging
            console.log("Form data being sent:", {
                id: !isNewItem ? testimonialData.id : 'new item',
                client_name: testimonialData.client_name,
                client_role: testimonialData.client_role,
                testimonial: testimonialData.testimonial,
                rating: testimonialData.rating,
                active: testimonialData.active ? 1 : 0,
                hasImage: !!selectedImageFile
            });
            
            // Determine which endpoint to use based on whether it's a new item or an update
            const endpoint = isNewItem ? "admin/testimonial/add" : "admin/testimonial/update";
            
            axios.post(config.api.url + endpoint, formData, {
                crossdomain: true,
                headers: {
                    api: config.api.key,
                    auth: account?.auth_token || cookies['account-canto']
                    // Note: Don't set Content-Type when sending FormData
                    // The browser will set it automatically with the correct boundary
                }
            })
            .then((response) => {
                console.log("Full API response:", response.data);
                const { success, response: responseMsg } = response.data;
                
                if (success) {
                    console.log(isNewItem ? "Add successful:" : "Update successful:", responseMsg);
                    closeSlideout();
                    fetchTestimonials();
                    showToast({ 
                        message: isNewItem ? 'Testimonial added successfully!' : 'Testimonial updated successfully!', 
                        type: 'success' 
                    });
                } else {
                    console.log("Error from server:", responseMsg);
                    showToast({ 
                        message: `Error ${isNewItem ? 'adding' : 'updating'} testimonial: ` + responseMsg, 
                        type: 'error' 
                    });
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error(isNewItem ? "Add error:" : "Update error:", error);
                showToast({ 
                    message: `Error ${isNewItem ? 'adding' : 'updating'} testimonial. Please try again.`, 
                    type: 'error' 
                });
                setLoading(false);
            });
        };

        return (
            <div className="EditTestimonialContainer">
                <div className="EditTestimonialOptions">
                    <div className="EditTestimonialOption">
                        <div className="EditTestimonialOptionTitle">Client Image</div>
                        <div className="EditTestimonialOptionContent">
                            <div 
                                className="EditTestimonialImagePreview" 
                                style={{ backgroundImage: `url(${selectedImagePreview})` }}
                                onClick={handleSelectImage}
                            >
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    style={{ display: 'none' }} 
                                    onChange={handleImageChange} 
                                    accept="image/*"
                                />
                            </div>
                            <div className="EditTestimonialImageText">Click to change image</div>
                        </div>
                    </div>

                    <div className="EditTestimonialOption">
                        <div className="EditTestimonialOptionTitle">Client Name</div>
                        <div className="EditTestimonialOptionContent">
                            <input 
                                type="text" 
                                name="client_name" 
                                value={testimonialData.client_name} 
                                onChange={handleChange} 
                                className="EditTestimonialInput"
                            />
                        </div>
                    </div>

                    <div className="EditTestimonialOption">
                        <div className="EditTestimonialOptionTitle">Client Role</div>
                        <div className="EditTestimonialOptionContent">
                            <input 
                                type="text" 
                                name="client_role" 
                                value={testimonialData.client_role} 
                                onChange={handleChange} 
                                className="EditTestimonialInput"
                            />
                        </div>
                    </div>

                    <div className="EditTestimonialOption">
                        <div className="EditTestimonialOptionTitle">Testimonial</div>
                        <div className="EditTestimonialOptionContent">
                            <textarea 
                                name="testimonial" 
                                value={testimonialData.testimonial} 
                                onChange={handleChange} 
                                className="EditTestimonialTextarea"
                                rows="5"
                            />
                        </div>
                    </div>

                    <div className="EditTestimonialOption">
                        <div className="EditTestimonialOptionTitle" onClick={() => console.log(testimonialData)}>Active</div>
                        <div className="EditTestimonialOptionContent">
                            <label className="EditTestimonialCheckboxLabel">
                                <input 
                                    type="checkbox" 
                                    name="active" 
                                    checked={testimonialData.active} 
                                    onChange={handleChange} 
                                    className="EditTestimonialCheckbox"
                                />
                                <span>Show on website</span>
                            </label>
                        </div>
                    </div>

                    <div className="EditTestimonialActions">
                        <button 
                            className="EditTestimonialSaveButton" 
                            onClick={handleSaveChanges}
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    return (
        <div className='TestimonialsAdmin'>
            <div className='TestimonialsHeader'>
                <div className='TestimonialsHeaderTitle'>
                    <h1>Testimonials</h1>
                    <p>Manage client testimonials that appear on your website</p>
                </div>
                <div className='TestimonialsHeaderActions'>
                    <button className='TestimonialsRefreshButton' onClick={fetchTestimonials}>
                        <IoMdRefresh />
                        <span>Refresh</span>
                    </button>
                    <button 
                        className='TestimonialsAddButton'
                        onClick={() => {
                            openSlideout({
                                title: 'Add New Testimonial',
                                content: <EditTestimonial fetchTestimonials={fetchTestimonials}/>
                            });
                        }}
                    >
                        <FaPlusCircle />
                        <span>Add New</span>
                    </button>
                    <button 
                        className='TestimonialsAddButton'
                        onClick={saveOrder}
                        disabled={loading}
                    >
                        <span>Save Order</span>
                    </button>
                </div>
            </div>

            <div className='TestimonialsContent'>
                <div className='TestimonialsContentNavigation'>
                    <div className='TestimonialsContentNavigationItem Active'>Active</div>
                    <div className='TestimonialsContentNavigationItem Image'>Image</div>
                    <div className='TestimonialsContentNavigationItem Name'>Client Name</div>
                    <div className='TestimonialsContentNavigationItem Role'>Client Role</div>
                    <div className='TestimonialsContentNavigationItem Testimonial'>Testimonial</div>
                    {/* <div className='TestimonialsContentNavigationItem Date'>Date</div> */}
                </div>
                <div className='TestimonialsScroll'>
                    {testimonials.map((testimonial, index) => {
                        return (
                            <div className='TestimonialItem' key={testimonial.id}>
                                <div className="TestimonialItemDetailsText First Active" onClick={() => console.log(testimonial)}>
                                    <span className={`TestimonialItemDetailsTextOption Circle ${testimonial.active > 0 ? 'Success' : 'Error'}`}>
                                        {testimonial.active > 0
                                        ? <RiCheckFill style={{ fontSize: 12 }} />
                                        : <RiCloseFill style={{ fontSize: 12 }} />
                                        }
                                    </span>
                                </div>
                                <div className="TestimonialItemDetailsText Image">
                                    <div 
                                        className="TestimonialItemDetailsTextImage" 
                                        style={{ backgroundImage: `url(${testimonial.client.url})` }} 
                                    />
                                </div>
                                <div className="TestimonialItemDetailsText Name Bold">{testimonial.client.name}</div>
                                <div className="TestimonialItemDetailsText Role">{testimonial.client.role}</div>
                                <div className="TestimonialItemDetailsText TestimonialText">
                                    {testimonial.testimonial.length > 100 
                                        ? `${testimonial.testimonial.substring(0, 100)}...` 
                                        : testimonial.testimonial
                                    }
                                </div>
                                {/* <div className="TestimonialItemDetailsText Date">{formatTimestamp(testimonial.timestamp)}</div> */}

                                <div className="TestimonialItemOptions">
                                    <a 
                                        onClick={() => {
                                            // Toggle active status
                                            console.log(`Toggle active status for testimonial ${testimonial.id}`);
                                        }} 
                                        className={`TestimonialItemOptionsButton ${testimonial.active === 1 ? 'DeleteInverted' : 'AddInverted'}`}
                                    >
                                        {testimonial.active === 1
                                        ? <RiToggleFill style={{ fontSize: 14 }} />
                                        : <RiToggleLine style={{ fontSize: 14 }} />
                                        }
                                        <span className="TestimonialItemOptionsButtonText">
                                            {testimonial.active === 1 ? 'Deactivate' : 'Activate'}
                                        </span>
                                    </a>

                                    <a 
                                        onClick={() => moveItem(index, -1)}
                                        className="TestimonialItemOptionsButton Edit"
                                    >
                                        <RiArrowUpSLine style={{ fontSize: 16 }} />
                                        <span className="TestimonialItemOptionsButtonText">Up</span>
                                    </a>
                                    <a 
                                        onClick={() => moveItem(index, 1)}
                                        className="TestimonialItemOptionsButton Edit"
                                    >
                                        <RiArrowDownSLine style={{ fontSize: 16 }} />
                                        <span className="TestimonialItemOptionsButtonText">Down</span>
                                    </a>

                                    <a 
                                        onClick={() => {
                                            openSlideout({
                                                title: 'Edit Testimonial',
                                                content: <EditTestimonial testimonial={testimonial} fetchTestimonials={fetchTestimonials}/>
                                            });
                                        }} 
                                        className="TestimonialItemOptionsButton Edit"
                                    >
                                        <RiFileShield2Line style={{ fontSize: 14 }} />
                                        <span className="TestimonialItemOptionsButtonText">Edit</span>
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className='TestimonialsPaginationWrapper'>
                    {/* Pagination will be added here when needed */}
                </div>
            </div>
        </div>
    );
}
